import { Button, ButtonProps } from "./button";
import { LucideIcon } from "lucide-react";
import type { Enduser, InboxThread } from "@tellescope/types-models";
import { ChannelIcon } from "./channel-icon";
import { AvatarGroup, AvatarItem } from "./atoms";
import { cn, formatDateTime, formatInitials, formatName } from "@tellescope/utils";
import { useMemo, ComponentProps, ElementType, ReactNode, HTMLAttributes } from "react";

export type DraftsAndSentItemProps<
  EnduserLike extends Pick<Enduser, "fname" | "lname" | "avatar" | "id">,
  T extends ElementType = "div",
> = {
  title: string;
  timestamp: Date | string | ReactNode;
  endusers: EnduserLike[];
  preview: string;
  channel: InboxThread["type"];
  actions?: (Partial<Omit<ButtonProps, "children">> & { icon: LucideIcon; key: string })[];
  showActions?: boolean | "onHover";
  className?: string;
  groupPosition?: "top" | "bottom" | "middle";
  altComponent?: T;
  altComponentProps?: Omit<ComponentProps<T>, "className" | "children">;
} & HTMLAttributes<T>;

export const DraftsAndSentItem = <
  EnduserLike extends Pick<Enduser, "fname" | "lname" | "avatar" | "id">,
  T extends ElementType = "div",
>({
  title,
  timestamp,
  endusers,
  preview,
  channel,
  actions,
  showActions: providedShowActions = "onHover",
  className,
  groupPosition,
  altComponent,
  altComponentProps,
  ...rest
}: DraftsAndSentItemProps<EnduserLike, T>) => {
  const showActions = actions?.length ? providedShowActions : false;
  const avatarItems = useMemo(
    () =>
      endusers.reduce<AvatarItem[]>((acc, enduser) => {
        acc.push({
          id: enduser.id,
          avatar: enduser.avatar,
          initials: formatInitials(enduser),
          name: formatName(enduser),
        });
        return acc;
      }, []),
    [endusers]
  );

  const Component = altComponent || "div";
  const componentProps = (altComponent ? { ...altComponentProps, ...rest } : rest) as ComponentProps<typeof Component>;

  return (
    <Component
      {...componentProps}
      className={cn(
        "group relative w-full h-[74px] p-4",
        "bg-white box-border border",
        groupPosition === "top"
          ? "rounded-t-[14px] rounded-b-none border-b-0"
          : groupPosition === "bottom"
          ? "rounded-t-none rounded-b-[14px]"
          : groupPosition === undefined
          ? "rounded-[14px]"
          : "border-b-0",
        !actions?.length && "hover:bg-gray-100",
        className
      )}
      data-testid="drafts-sent-item"
    >
      <div className="flex items-center h-full gap-3">
        <AvatarGroup items={avatarItems} />

        <div className={`flex flex-col justify-center flex-1 min-w-0 transition-all duration-75`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold leading-5 text-[#0a0a0a] overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 mr-3">
              {title}
            </div>
            <div className={cn("flex items-center gap-2 flex-shrink-0", showActions && "group-hover:hidden")}>
              <span className="text-sm leading-5 text-[#737373] whitespace-nowrap">
                {typeof timestamp === "string"
                  ? timestamp
                  : timestamp instanceof Date
                  ? formatDateTime(timestamp, "MM/DD/YYYY at hh:mm a", "always")
                  : timestamp}
              </span>
              <ChannelIcon channel={channel} />
            </div>
          </div>
          <div className="text-sm leading-5 text-[#737373] overflow-hidden text-ellipsis whitespace-nowrap">
            {preview}
          </div>
        </div>

        {!!showActions && !!actions?.length && (
          <div
            style={{ "--actions-width": `${(actions.length ?? 0) * 36}px` }}
            className={cn(
              `group-hover:flex items-center space-x-0.5 flex-shrink-0 transition-all w-0 group-hover:w-[var(--actions-width)]`,
              showActions === "onHover" ? "overflow-hidden" : ""
            )}
          >
            {actions?.map(({ key, icon: Icon, className, ...props }) => (
              <Button
                key={key}
                variant="ghost"
                size="icon"
                className={cn("w-9 h-9 text-muted-foreground", className)}
                {...props}
              >
                {<Icon className="w-4 h-4" />}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Component>
  );
};

DraftsAndSentItem.displayName = "DraftsAndSentItem";
